//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Enums } from '../../models/common'
import { IOpObject } from '../../models/util/interfaces'
import { OpPackage, OpDataTypeInGroup, OpSupplier, OpFile } from '../../models/core'

import { ColumnModel } from '../../modules/api/api.models'
import { CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'
import { Language } from '../system/system.service.interfaces'

import { ToastHelper, ConvertHelper, LocalizationHelper, LogHelper, DataTypeGroupComponent, CommonHelper, ConfigHelper, TemplateHelper } from '../../helpers'

import { GridService, GRID_SERVICE_NAME } from '../grid'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { SystemService, SYSTEM_SERVICE_NAME } from '../system'
import { SupplierEditInitAdditionalParams } from '../../modules/suppliers/create-edit/create-edit-supplier.interfaces'
import { DataProviderRequest } from '../../data-provider'


//#region Public Types

export const SUPPLIER_SERVICE_NAME = 'SupplierService'
export const SUPPLIER_SERVICE_PROVIDER_NAME = 'SupplierServiceProvider'

//#endregion

export class SupplierService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Supplier]' }
    public static get FILE_NAME_PREFIX(): string { return 'suppliers' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdSupplier }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Supplier }

    //#endregion


    //#region Ctor

    constructor(
        private gridService: GridService,
        private userSessionService: UserSessionService,
        private systemService: SystemService
    ) { }

    //#endregion


    //#region Columns

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
        ], SupplierService.SIMAX_MODULE_NAME,
            {
                groupReferenceType: Enums.ReferenceType.IdProfile
            })
    }

    //#endregion

    //#region CreateEdit

    public openCreate(): void {
        const createEditOptions: CreateEditInitParams<SupplierEditInitAdditionalParams> = this.getCreateEditOptions(0, undefined, undefined)

        this.systemService.openCreateEdit(createEditOptions)
    }

    public openEdit(idSupplier: number, supplier: OpSupplier = null): void {
        const createEditOptions: CreateEditInitParams<SupplierEditInitAdditionalParams> = this.getCreateEditOptions(idSupplier, supplier, undefined, false)

        this.systemService.openCreateEdit(createEditOptions)
    }

    //#endregion


    //#region Create

    public createOpPackageFromIOpObject(sourceIOpObject: IOpObject, sourcePackage: OpPackage): OpPackage {
        if (sourceIOpObject == null) {
            return
        }

        const newPackage: OpPackage = OpPackage.fromIOpObject(sourceIOpObject)

        newPackage.idPackageStatus = Enums.PackageStatus.New

        if (newPackage.idPackage === 0) {
            newPackage.creationDate = new Date()
        }
        else {
            if (sourcePackage != null) {
                newPackage.creationDate = sourcePackage.creationDate
            }
        }

        return newPackage
    }

    //#endregion

    //#region Delete

    public deleteSupplier(idSupplier: number): JQueryPromise<void> {
        return this.userSessionService
            .getDataProvider()
            .Supplier
            .deleteSupplier(idSupplier)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, SupplierService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, SupplierService.SIMAX_MODULE_NAME))
                LogHelper.warn(SupplierService.LOG_PREFIX, `Unable to delete supplier for id ${idSupplier}`, error)
            })
    }

    //#endregion


    //#region Get

    public getSupplier(idSupplier: number) {
        const promise: DataProviderRequest<OpSupplier> = this.userSessionService
            .getDataProvider()
            .Supplier
            .getSupplier(idSupplier)

        promise.then((supplier: OpSupplier) => {
            if (supplier != null) {
                return supplier
            }

            return $.Deferred().reject(null).promise()
        })

        promise.catch((error: any) => {
            ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            LogHelper.warn(SupplierService.LOG_PREFIX, `Unable to load supplier for id ${idSupplier}`, error)

            return null
        })

        return promise
    }

    public getSupplierLogo(idSupplier: number): JQueryPromise<OpFile> {
        return this.userSessionService
            .getDataProvider()
            .Supplier
            .getSupplierLogo(idSupplier)
            .then((logo: OpFile) => {
                return logo
            })
            .fail((error: any) => {
                LogHelper.warn(SupplierService.LOG_PREFIX, `Unable to load logo supplier for id ${idSupplier}`, error)
            })
    }

    public getMessageFileContent(idFile: number): JQueryPromise<OpFile> {
        return this.userSessionService
            .getDataProvider()
            .Supplier
            .getFiles([idFile])
            .then((opFiles: OpFile[]) => {
                if (_.size(OpFile) > 0) {
                    return opFiles[0]
                }

                return null
            })
            .fail((error: any) => {
                LogHelper.warn(SupplierService.LOG_PREFIX, `Unable to load message content for file id ${idFile}`, error)
            })
    }

    //#endregion

    //#region Options

    public getCreateEditOptions(referenceObjectId: number = 0,
        referenceObject: OpSupplier = null,
        selectedTabType: Enums.SimaxCreateEditSelectedTabType = Enums.SimaxCreateEditSelectedTabType.BaseParameters,
        isCopy: boolean = false,
    ): CreateEditInitParams {
        return {
            createEditSelectedTabType: selectedTabType,
            idReferenceType: SupplierService.REFERENCE_TYPE,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            moduleName: SupplierService.SIMAX_MODULE_NAME,
            referenceObject: referenceObject,
            referenceObjectId: referenceObjectId,
            sections: null,
            useBusinessObjects: true,
            isCopy: isCopy
        }
    }

    public getMessagesGridColumns(onDelete: (file: OpFile) => void): ColumnModel[] {
        const physicalFileName: ColumnModel = new ColumnModel()
        const description: ColumnModel = new ColumnModel()
        const insertDate: ColumnModel = new ColumnModel()

        physicalFileName.idName = 'physicalFileName'
        physicalFileName.name = 'PhysicalFileName'
        physicalFileName.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)

        description.idName = 'description'
        description.name = 'Description'
        description.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)

        insertDate.idName = 'insertDate'
        insertDate.name = 'InsertDate'
        insertDate.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FillingDate)
        insertDate.type = Enums.DataTypeClass.Datetime

        const toolbarColumn: ColumnModel = this.gridService.getToolbarColumn<OpFile>((gridObject: any) => {
            const file: OpFile = this.gridService.getSourceObjectFromGridObject<OpFile>(gridObject, Enums.ReferenceType.IdFile)

            if (file != null) {
                return {
                    items: [{
                        location: 'center',
                        widget: 'dxButton',
                        options: {
                            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Delete),
                            icon: 'close',

                            onClick: (e: any) => {
                                if (_.isFunction(onDelete)) {
                                    onDelete(file)
                                }
                            }
                        }
                    }]
                }
            }

            return null
        });

        toolbarColumn.name = 'deleteTemplate'
        toolbarColumn.caption = ''//LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EditReferenceObject)
        toolbarColumn.width = 70

        return [physicalFileName, description, insertDate, toolbarColumn]
    }

    //#endregion


    //#region Parameters 

    public getBasicParametrsDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSessionService.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                SupplierService.SIMAX_MODULE_NAME
            )
    }

    //#endregion

    //#region Save

    public saveSupplier(toSave: OpSupplier, isBulkSave: boolean = false): JQueryPromise<OpSupplier> {
        return this.userSessionService
            .getDataProvider().Supplier
            .saveSupplier(toSave)
            .then((savedObject: OpSupplier) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, SupplierService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.packageSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, SupplierService.SIMAX_MODULE_NAME))
                LogHelper.warn(SupplierService.LOG_PREFIX, `Unable to save supplier with id ${toSave.idSupplier}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    public saveSupplierLogo(idSupplier: number, file: OpFile): JQueryPromise<OpFile> {
        return this.userSessionService
            .getDataProvider()
            .Supplier
            .saveSupplierLogo(idSupplier, file)
            .then((data: OpFile) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.IssueAttachmentSave)) //TODO
                return data
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.IssueAttachmentSaveError))
                LogHelper.log(SupplierService.LOG_PREFIX, `Unable to save logo. Error: ${errorMsg}.`)

                return $.Deferred<OpFile>().reject(error).promise()
            })
    }


    public saveSupplierMessageFile(idSupplier: number, file: OpFile, idlanguage: number): JQueryPromise<OpFile> {
        return this.userSessionService
            .getDataProvider()
            .Supplier
            .saveSupplierMessageFile(idSupplier, file, idlanguage)
            .then((data: OpFile) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TemplateSaved))
                return data
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TemplateSaveError))
                LogHelper.log(SupplierService.LOG_PREFIX, `Unable to save message file. Error: ${errorMsg}.`)

                return $.Deferred<OpFile>().reject(error).promise()
            })
    }

    public deleteSupplierMessageFile(idSupplier: number, file: OpFile, idlanguage: number): JQueryPromise<void> {
        file.opState = Enums.OpChangeState.Delete
        
        return this.userSessionService
            .getDataProvider()
            .Supplier
            .saveSupplierMessageFile(idSupplier, file, idlanguage)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TemplateDeleted))
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TemplateDeleteError))
                LogHelper.log(SupplierService.LOG_PREFIX, `Unable to delete message file. Error: ${errorMsg}.`)

                return $.Deferred<OpFile>().reject(error).promise()
            })
    }

    //#endregion
}


export class SupplierServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [GRID_SERVICE_NAME, USER_SESSION_SERVICE_NAME, SYSTEM_SERVICE_NAME, function (gridService: GridService, userSessionService: UserSessionService, systemService: SystemService) {
        return new SupplierService(gridService, userSessionService, systemService);
    }]

    //#endregion
} 